import { Switch, SwitchProps, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import UserService from "../../api/UserService";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon/LeftArrowIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { RootState } from "../../store";
import { userSliceActions } from "../../store/user-slice";
import styles from "../../styles/email-notifications.module.css";

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#11A8B8",
        opacity: 1,
        border: 0,
        ...theme.applyStyles("dark", {
          backgroundColor: "#2ECA45",
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[600],
      }),
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      ...theme.applyStyles("dark", {
        opacity: 0.3,
      }),
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
  },
}));

export const EmailNotifications = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector((state: RootState) => state.user);
  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );

  const [checked, setChecked] = useState<boolean>(
    !!user.currentUser?.notificationSettings
  );

  useEffect(() => {
    setChecked(!!user.currentUser?.notificationSettings);
  }, [user.currentUser?.notificationSettings]);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;
    setChecked(newChecked);

    const userId = user.currentUser?.id;
    if (!userId) return;

    try {
      if (newChecked) {
        await UserService.UpdateNotificationSettins(userId, {
          notificationSettings: [
            {
              subject: "wirelessTrap",
              emailReminder: "none",
              emailNotification: "asap",
            },
          ],
        });
      } else {
        await UserService.UpdateNotificationSettins(userId, {
          notificationSettings: null,
        });
      }

      const { data } = await UserService.GetCurrentUser();
      dispatch(userSliceActions.setCurrentUser(data));
    } catch (error) {
      console.error("Error updating notification settings:", error);
    }
  };

  const handleBackButtonClicked = () => {
    navigate(-1);
  };

  return (
    <AppLayout
      withBackButton={true}
      hideHeader={isCustomerAdmin}
      onGoBack={handleBackButtonClicked}
    >
      <div className={styles["customer-um"]} id="scrollable-wrapper">
        <div className={styles["customer-um__heading"]}>
          <div className={styles["customer-um__back"]}>
            {isCustomerAdmin && (
              <LeftArrowIcon
                handleBackButtonClicked={handleBackButtonClicked}
              />
            )}
            <p className={styles["customer-um__title"]}>
              {t("EmailNotifications")}
            </p>
          </div>
        </div>

        <div
          className={`${styles["settings-bottom__feedback-link"]} ${styles["flex"]}`}
        >
          <h2 className={styles["item"]}>{t("EnableEmailNotifications")}</h2>
          <IOSSwitch checked={checked} onChange={handleToggle} />
        </div>
      </div>
    </AppLayout>
  );
};
